import {
  VITE_API_URL,
  VITE_DATADOG_ENV,
  VITE_ENV_NAME,
  VITE_RELEASE_VERSION,
} from '@/config';
import type { MyProfileModel } from '@/features/authentication/_models/me';
import {
  excludeDepStack,
  isPS,
  isProd,
  isProxyRequired,
  replaceLocationHost,
} from '@/utils';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { z } from 'zod';

const ignoreEnvs = ['local', 'mock', 'unknown'];
const ignoreHostnames = ['localhost'];
const ignoreVersions = ['unknown'];
const enableDatadog =
  !ignoreEnvs.includes(VITE_ENV_NAME) &&
  !ignoreHostnames.includes(location.hostname) &&
  !ignoreVersions.includes(VITE_RELEASE_VERSION) &&
  // 公開サイトの本番は除外する
  !(isPS() && isProd());

const DatadogDefaultMetadata = {
  environment: VITE_DATADOG_ENV.split('-')[0],
  product:
    VITE_DATADOG_ENV.split('-').length > 1
      ? VITE_DATADOG_ENV.split('-')[1]
      : 'bns',
};

const datadogProxy = isProxyRequired()
  ? (options: { path: string; parameters: string }) => {
      return replaceLocationHost(
        `${location.origin}/datadog${options.path}?${options.parameters}`,
      );
    }
  : undefined;

const initDatadog = () => {
  // Datadog RUM 初期化
  datadogRum.init({
    applicationId: 'bbbc7289-c51e-4710-9abc-69fd7bf8c122',
    clientToken: 'pub55e7ae7d597cd8b372deb2d6b074fb2d',
    site: 'ap1.datadoghq.com',
    service: 'bns',
    env: VITE_DATADOG_ENV,
    allowedTracingUrls: [
      { match: VITE_API_URL, propagatorTypes: ['datadog'] },
      { match: 'http://localhost:8100', propagatorTypes: ['datadog'] },
      { match: 'http://localhost', propagatorTypes: ['datadog'] },
    ],
    version: VITE_RELEASE_VERSION,
    // RS 本番はユーザーが多いため、コスト削減のため、サンプリング率を下げる
    sessionSampleRate: isProd() ? 20 : 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 100,
    trackUserInteractions: true,
    beforeSend: (event, context) => {
      // collect a RUM resource's response headers
      if (
        event.context &&
        event.type === 'resource' &&
        event.resource.type === 'fetch' &&
        'response' in context &&
        context.response
      ) {
        event.context.responseHeaders = Object.fromEntries(
          context.response.headers,
        );
      }

      return true;
    },
    trackResources: true,
    trackLongTasks: true,
    traceContextInjection: 'sampled',
    silentMultipleInit: true,
    storeContextsAcrossPages: true,
    compressIntakeRequests: true,
    // 本番の場合、基本的に mask する
    defaultPrivacyLevel: isProd() ? 'mask' : 'mask-user-input',
    enablePrivacyForActionName: true,
    proxy: datadogProxy,
  });

  // Datadog RUM Session Replay 開始
  datadogRum.startSessionReplayRecording();
  datadogRum.setGlobalContext(DatadogDefaultMetadata);

  datadogLogs.init({
    clientToken: 'pub55e7ae7d597cd8b372deb2d6b074fb2d',
    site: 'ap1.datadoghq.com',
    service: 'bns',
    env: VITE_DATADOG_ENV,
    version: VITE_RELEASE_VERSION,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    telemetrySampleRate: 100,
    silentMultipleInit: true,
    // ref. https://docs.datadoghq.com/ja/logs/log_collection/javascript/#%E3%82%B3%E3%83%B3%E3%83%86%E3%82%AD%E3%82%B9%E3%83%88%E3%81%AE%E3%83%A9%E3%82%A4%E3%83%95%E3%82%B5%E3%82%A4%E3%82%AF%E3%83%AB
    storeContextsAcrossPages: true,
    useSecureSessionCookie: true,
    usePartitionedCrossSiteSessionCookie: true,
    proxy: datadogProxy,
    beforeSend: (log) => {
      const ignore =
        // 404
        (log.http && log.http.status_code === 404) ||
        // React Router or AxiosError not via logger
        (log.origin !== 'logger' &&
          (log.message.includes('React Router') ||
            // なぜか、log.error?.kind が 'AxiosError' にならない場合があるので、message や stack でも判定する
            log.error?.message?.includes('AxiosError') ||
            log.error?.stack?.includes('AxiosError') ||
            log.error?.kind === 'AxiosError'));

      if (ignore) {
        return false;
      }

      // network に関するエラーは、warn にする
      const isNetworkError =
        log.origin === 'network' ||
        log.message?.includes('Network Error') ||
        log.error?.message?.includes('Network Error');
      if (isNetworkError || log.message.startsWith('Warning')) {
        log.status = 'warn';
      }

      // インフラ、ネットワークの問題であるエラーは、warn にする
      const isInfrastructureError =
        log.http && [502, 503, 504].includes(log.http.status_code);
      if (isInfrastructureError) {
        log.status = 'warn';
      }

      if (log.error?.kind === 'ZodError' && log.origin !== 'logger') {
        log.error.message = log.message;
        log.message = 'failed to parse';
      }

      // When using Datadog Error Tracking, the deps stack must be excluded for proper tracking for not logger.
      if (log.error) {
        log.error.stack = excludeDepStack(log.error.stack);
      }
      return true;
    },
  });
  datadogLogs.setGlobalContext(DatadogDefaultMetadata);
};

if (enableDatadog) {
  initDatadog();
}

export const setUser = (
  userId: string,
  profile?: z.infer<typeof MyProfileModel>,
) => {
  datadogRum.setUser({
    id: userId,
    tenant_id: profile?.tenantId,
    tenant: profile?.tenant,
    is_active: profile?.isActive,
    organization_ids: profile?.organizationIds,
    role: profile?.role,
  });
  datadogLogs.setUser({
    id: userId,
    tenant_id: profile?.tenantId,
    tenant: profile?.tenant,
    is_active: profile?.isActive,
    organization_ids: profile?.organizationIds,
    role: profile?.role,
  });
};
